import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import {
  EarningsForecastDataTable,
  TableDataCell,
  TableWrapper,
  FutureEstimateRow,
} from './styled';
import { getFutureRevenueGrowthRawDataProps } from '../../redux/selectors';
import { useSelector, shallowEqual } from 'react-redux';
import { useMemo } from 'react';
import { separateByThreeDigit } from '@/utilities/formatting';
import ListTruncator from '@/components/ListTruncator';
import { useSummary } from '@/hooks/useCompany';
import { useFutureTranslation } from '../../hooks/useFutureTranslation';

const EarningsAndRevenueGrowthTable = () => {
  const summary = useSummary();
  const companyId = summary.status === 'success' ? summary.companyId : '';
  const uniqueSymbol = summary.status === 'success' ? summary.uniqueSymbol : '';
  const {
    futureEstimates,
    pastFinancialData,
    reportingUnit,
    reportingCurrencyISO,
    viewMode,
  } = useSelector(
    (state) => getFutureRevenueGrowthRawDataProps(state, { companyId }),
    shallowEqual
  );
  const combinedData = useMemo(
    () => futureEstimates.concat(pastFinancialData),
    [futureEstimates, pastFinancialData]
  );

  const t = useFutureTranslation();

  const table = (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('revenue.table.heading'),
          about: t('revenue.table.description', {
            uniqueSymbol,
            reportingCurrencyISO,
            reportingUnit,
          }),
        }}
      />
      <TableWrapper>
        <EarningsForecastDataTable data-cy-id="earnings-and-revenue-growth-table">
          <caption>
            {t('revenue.table.description', {
              uniqueSymbol,
              reportingCurrencyISO,
              reportingUnit,
            })}
          </caption>
          <thead>
            <tr>
              <th>{t('revenue.table.header.date')}</th>
              <th>{t('revenue.table.header.revenue')}</th>
              <th>{t('revenue.table.header.earnings')}</th>
              <th>{t('revenue.table.header.freeCashFlow')}</th>
              <th>{t('revenue.table.header.cash')}</th>
              <th>{t('revenue.table.header.analysts')}</th>
            </tr>
          </thead>
          <tbody>
            {futureEstimates.map(({ data, dateKey }, i) => {
              return (
                <FutureEstimateRow
                  key={i}
                  data-cy-id={`earnings-and-revenue-growth-row-${i}`}
                >
                  <td>{new Date(dateKey).toLocaleDateString()}</td>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <TableDataCell key={i} darken={!data[i]}>
                      {data[i] ? separateByThreeDigit(data[i], 0) : 'N/A'}
                    </TableDataCell>
                  ))}
                </FutureEstimateRow>
              );
            })}
            {pastFinancialData.map(({ data, dateKey }, i) => {
              return (
                <tr key={i} data-cy-id={`earnings-and-revenue-growth-row-${i}`}>
                  <td>{new Date(dateKey).toLocaleDateString()}</td>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <TableDataCell key={i} darken={!data[i]}>
                      {data[i] ? separateByThreeDigit(data[i], 0) : 'N/A'}
                    </TableDataCell>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </EarningsForecastDataTable>
      </TableWrapper>
    </>
  );

  return combinedData.length > 9 ? (
    <div data-cy-id="earnings-and-revenue-growth-table-truncator">
      <ListTruncator viewMode={viewMode} minHeight={400}>
        {table}
      </ListTruncator>
    </div>
  ) : (
    table
  );
};
export default EarningsAndRevenueGrowthTable;
